/**
 * Turn a check's raw `detail` blob into scannable pieces for the Validation tab.
 *
 * The checks emit developer-facing detail strings — long "label: a; b; c;
 * …(+704 more)" lists and internal identifiers. The tab now leads with the
 * plain-language `checkCopy.flag` and uses THIS to render the specific offending
 * items as a clean list instead of the raw run-on string. Presentation only —
 * no check logic changes.
 */

/**
 * Extract the offending items from a detail string.
 *
 * Handles the two shapes the checks produce:
 *   "label: item; item; item"          -> after the first ": "
 *   "…prose… e.g. item; item"          -> after "e.g. "
 * A sentence-style detail with no list (e.g. an eligibility count statement)
 * yields no items, so the row shows only its plain flag.
 *
 * The checks truncate long lists server-side with "(+N more)", so `moreCount`
 * is items that exist but aren't in the string — surfaced honestly, never
 * fabricated.
 */
export function parseDetailItems(detail: string | undefined): { items: string[]; moreCount: number } {
  if (!detail) return { items: [], moreCount: 0 };
  let s = detail.trim();

  let moreCount = 0;
  const m = s.match(/\(\+(\d+)\s*more\)/i);
  if (m && m.index !== undefined) {
    moreCount = parseInt(m[1], 10);
    s = s.slice(0, m.index).replace(/[;,]?\s*[….]*\s*$/, "").trim();
  }

  const egIdx = s.search(/\be\.g\.\s/i);
  if (egIdx >= 0) {
    s = s.slice(egIdx).replace(/^e\.g\.\s*/i, "");
  } else {
    const colon = s.indexOf(": ");
    if (colon >= 0) s = s.slice(colon + 2);
    else return { items: [], moreCount }; // sentence-style: no discrete items
  }

  const items = s.split(/;\s+/).map((x) => x.trim()).filter(Boolean);
  return { items, moreCount };
}

/**
 * Light jargon → plain substitutions for the item text that still shows. Small
 * and conservative: we never invent a human label we don't have (internal
 * CODE:VARIABLE ids stay as-is, just presented as supporting detail).
 */
const REPLACEMENTS: Array<[RegExp, string]> = [
  [/\s*≈\s*/g, " → "],
  [/studySetup\.edcSystem/g, "the EDC system"],
  [/\bfield_name\b/g, "field name"],
  [/\bform_name\b/g, "form name"],
  [/\bpage_name\b/g, "page name"],
  [/\bcrfQuestionIds\b/g, "source link"],
  [/\bsingle_select\b/g, "single-select"],
  [/\bresponse_type\b/g, "response type"],
  [/×(\d+)/g, " ($1)"], // "Time×5" -> "Time (5)", "SUPPAE×2" -> "SUPPAE (2)"
];

export function cleanText(s: string): string {
  let out = s;
  for (const [re, to] of REPLACEMENTS) out = out.replace(re, to);
  return out;
}
